import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import { formatDateRange } from "@/core/utils/date";
import { usePayPeriodNotes } from "@/payroll/period-notes/query";
import { useLockPayPeriod } from "./lock-query";
import type { PayPeriodLockTarget } from "./pay-period-lock";

export const PayPeriodLockDialog = ({
  businessId,
  period,
  open,
  onOpenChange,
}: PayPeriodLockTarget & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const lockPayPeriod = useLockPayPeriod(businessId, period.id);
  const notes = usePayPeriodNotes(businessId, period.start_date, {
    enabled: open,
    poll: open,
  });
  const openNoteCount = notes.data?.openNotes.length ?? 0;

  const handleLock = () => {
    lockPayPeriod.mutate(undefined, {
      onSuccess: () => {
        onOpenChange(false);
        toast.success("Pay period locked");
      },
      onError: (error: unknown) => {
        onOpenChange(false);
        toast.error("Pay period not locked", {
          description: getErrorMessage(error),
        });
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!lockPayPeriod.isPending) onOpenChange(nextOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Lock this pay period?</DialogTitle>
          <DialogDescription className="sr-only">
            Confirm locking this pay period
          </DialogDescription>
        </DialogHeader>
        <Stack align="start" gap={4}>
          <Text variant="label">
            {formatDateRange(period.start_date, period.end_date)}
          </Text>
          <Text tone="muted">
            Locked payroll cannot be rerun, and its workbook can no longer be
            edited. You can still read the Grid, PayPacks, and existing PackChat
            threads.
          </Text>
          <Text tone="muted">Only a Volca administrator can unlock it.</Text>
          {openNoteCount > 0 && (
            <Text role="alert" tone="destructive">
              Address each open payroll note or send it to the next open period
              before locking payroll.
            </Text>
          )}
          {notes.isError && (
            <Text role="alert" tone="destructive">
              Payroll notes could not be checked. Close this dialog and try
              again.
            </Text>
          )}
        </Stack>
        <DialogFooter>
          <Button
            variant="secondary"
            disabled={lockPayPeriod.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            disabled={
              lockPayPeriod.isPending ||
              notes.isPending ||
              notes.isError ||
              openNoteCount > 0
            }
            aria-busy={lockPayPeriod.isPending || undefined}
            onClick={handleLock}
          >
            {lockPayPeriod.isPending && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            Lock pay period
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
